import { db, storage } from '@/firebase/init'
import { BilagLibraryItem } from '@/globals/typescript/models/BilagLibraryItem'
import { deleteStorageFolder } from '@/globals/typescript/utils/deleteStorageFolder'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'
import type { IBilagLibraryItem } from '@team-milva/lab-models/bilagLibraryItem'
import { collection, getDocs, onSnapshot, query, where, writeBatch } from 'firebase/firestore'
import { ref as storageRef } from 'firebase/storage'
import { orderBy } from 'lodash-es'
import { defineStore } from 'pinia'
import type { TLibraryVisibilityFilter } from '@/globals/typescript/utils/libraryVisibilityFilter'

type TUpdateBilagLibraryItemValues = {
  internalTitle?: string
  displayTitle?: string
  locale?: string
  activeState?: IBilagLibraryItem['activeState']
}

export const useBilagLibraryItemStore = defineStore('bilagLibraryItem', {
  state: () => ({
    allBilagLibraryItems: [] as BilagLibraryItem[],
    unsubscribeAllBilagLibraryItems: null as (() => void) | null,
    languageFilter: [] as string[],
    visibilityFilter: 'active' as TLibraryVisibilityFilter,
  }),

  getters: {
    allBilagLibraryItemsLoaded: (state): boolean =>
      !!state.unsubscribeAllBilagLibraryItems,
    hasBilagLibraryItems: (state): boolean =>
      state.allBilagLibraryItems.length > 0,
  },

  actions: {
    getBilagLibraryItems(): Promise<BilagLibraryItem[]> {
      if (this.allBilagLibraryItemsLoaded) {
        return Promise.resolve(this.allBilagLibraryItems)
      }

      const accountStore = useAccountStore()
      if (!accountStore.labAccount?.id) {
        return Promise.resolve([])
      }

      return new Promise((resolve) => {
        let resolved = false

        const unsubscribe = onSnapshot(
          query(
            collection(db, 'Apps', '-lab-management-', 'BilagLibraryItems'),
            where('labAccountID', '==', accountStore.labAccount!.id)
          ),
          (snapshot) => {
            this.allBilagLibraryItems = orderBy(
              snapshot.docs.map(
                (docSnap) =>
                  new BilagLibraryItem(docSnap.data() as IBilagLibraryItem)
              ),
              [(x) => x.internalTitle.toLowerCase()],
              ['asc']
            )
            this.unsubscribeAllBilagLibraryItems = unsubscribe

            if (!resolved) {
              resolved = true
              resolve(this.allBilagLibraryItems)
            }
          }
        )
      })
    },

    async createBilagLibraryItem({
      bilagLibraryItemID,
      internalTitle,
      displayTitle,
      locale,
      storagePath,
      createdByUserID,
    }: {
      bilagLibraryItemID: string
      internalTitle: string
      displayTitle: string
      locale: string
      storagePath: string
      createdByUserID: string
    }) {
      const accountStore = useAccountStore()

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return await (managementApi as any)(
        'post',
        'lab-accounts/{labAccountID}/bilag-library',
        {
          path: { labAccountID: accountStore.labAccount?.id ?? '' },
          body: {
            bilagLibraryItemID,
            internalTitle,
            displayTitle,
            locale,
            storagePath,
            createdByUserID,
          },
        }
      )
    },

    async updateBilagLibraryItem(
      bilagLibraryItemID: string,
      values: TUpdateBilagLibraryItemValues
    ) {
      const accountStore = useAccountStore()

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'patch',
        'lab-accounts/{labAccountID}/bilag-library/{bilagLibraryItemID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            bilagLibraryItemID,
          },
          body: values,
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    // DEV: remove before launch
    async deleteAllBilagLibraryItems(): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const snap = await getDocs(
        query(
          collection(db, 'Apps', '-lab-management-', 'BilagLibraryItems'),
          where('labAccountID', '==', labAccountID)
        )
      )

      const batch = writeBatch(db)
      snap.docs.forEach((x) => batch.delete(x.ref))
      await batch.commit()

      // Best-effort — a Storage hiccup here must never leave the Firestore delete above undone.
      await deleteStorageFolder(
        storageRef(storage, `labAccounts/${labAccountID}/bilagLibrary`)
      ).catch(() => {})
    },

    resetStore(): void {
      this.allBilagLibraryItems = []
      if (this.unsubscribeAllBilagLibraryItems) {
        this.unsubscribeAllBilagLibraryItems()
        this.unsubscribeAllBilagLibraryItems = null
      }
      this.languageFilter = []
      this.visibilityFilter = 'active'
    },
  },
})
